import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativePageRoot } from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { Mail, Lock, Eye, EyeOff, User, AlertCircle, Loader, CheckCircle, Pill, Plus, X } from 'lucide-react';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;

  ${iosNativePageRoot}
`;

const AuthContainer = styled.div`
  width: 100%;
  max-width: 400px;
  animation: slideUp 0.5s ease-out;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h1 {
    font-family: ${theme.typography.fontFamily.secondary};
    font-size: ${theme.typography.fontSize['4xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary[600]};
    margin: 0;
  }
  
  p {
    color: ${theme.colors.text.secondary};
    margin-top: 0.5rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean; $hasTrailingIcon?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  padding: 0.875rem 1rem;
  /* Reserve space for inline icon so long text doesn't overlap - same as GlowBot / SignIn */
  padding-right: ${props => props.$hasTrailingIcon ? '3rem' : '1rem'};
  border: 2px solid ${props => props.$hasError ? theme.colors.status.error : theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
  
  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.875rem 1rem;
  padding-right: 2.5rem;
  border: 2px solid ${props => props.$hasError ? theme.colors.status.error : theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  appearance: auto;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
  
  &:invalid {
    color: ${theme.colors.text.tertiary};
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.text.tertiary};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.text.secondary};
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.status.error}10;
  border: 1px solid ${theme.colors.status.error}30;
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.status.success}10;
  border: 1px solid ${theme.colors.status.success}30;
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.status.success};
  font-size: ${theme.typography.fontSize.sm};
`;

const PasswordRequirements = styled.div`
  background: ${theme.colors.background.secondary};
  padding: 1rem;
  border-radius: ${theme.borderRadius.lg};
  margin-top: 0.5rem;
`;

const RequirementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RequirementItem = styled.li<{ $met: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${props => props.$met ? theme.colors.status.success : theme.colors.text.tertiary};
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.border.light};
  }
  
  span {
    color: ${theme.colors.text.tertiary};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  
  p {
    color: ${theme.colors.text.secondary};
    margin: 0;
  }
  
  a {
    color: ${theme.colors.primary[600]};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeight.medium};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoadingSpinner = styled(Loader)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SignUpPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dietaryPreference, setDietaryPreference] = useState<'vegetarian' | 'non-vegetarian' | null>(null);
  const [gender, setGender] = useState<'male' | 'female' | 'prefer_not_to_say' | null>(null);
  const [prescriptions, setPrescriptions] = useState<string[]>([]);
  const [newPrescription, setNewPrescription] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/';

  // Password validation
  const passwordRequirements = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(password) },
    { text: 'Contains number', met: /\d/.test(password) },
  ];

  const isPasswordValid = passwordRequirements.every(req => req.met);
  const doPasswordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('Please meet all password requirements.');
      return;
    }

    if (!doPasswordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    if (!dietaryPreference) {
      setError('Please select your dietary preference.');
      return;
    }

    if (!gender) {
      setError('Please select your gender.');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, name, dietaryPreference, gender);
      // Redirect to email verification page
      navigate('/verify-email', { state: { email } });
    } catch (error: any) {
      console.error('Sign up error:', error);
      
      // Handle specific Cognito errors
      const errorCode = error.code || error.name || '';
      const errorMessage = error.message || '';
      
      if (errorCode === 'UsernameExistsException' || errorMessage.includes('User already exists')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (errorCode === 'InvalidParameterException' || errorMessage.includes('Invalid email')) {
        setError('Please enter a valid email address.');
      } else if (errorCode === 'InvalidPasswordException' || errorMessage.includes('Password')) {
        setError('Password does not meet requirements.');
      } else if (errorMessage.includes('network')) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <AuthContainer>
        <Logo>
          <h1>GlowLens™</h1>
          <p>Join the glow revolution! Let's start your journey ✨</p>
        </Logo>

        <Card padding="lg">
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="name">Full Name</Label>
              <InputContainer>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  $hasError={!!error}
                  $hasTrailingIcon
                />
                <User size={20} style={{ 
                  position: 'absolute', 
                  right: '1rem', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: theme.colors.text.tertiary,
                  pointerEvents: 'none'
                }} />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="email">Email Address</Label>
              <InputContainer>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  $hasError={!!error}
                  $hasTrailingIcon
                />
                <Mail size={20} style={{ 
                  position: 'absolute', 
                  right: '1rem', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: theme.colors.text.tertiary,
                  pointerEvents: 'none'
                }} />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <InputContainer>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  required
                  $hasError={!!error}
                  $hasTrailingIcon
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </PasswordToggle>
              </InputContainer>
              
              {password && (
                <PasswordRequirements>
                  <RequirementList>
                    {passwordRequirements.map((req, index) => (
                      <RequirementItem key={index} $met={req.met}>
                        {req.met ? <CheckCircle size={16} /> : <div style={{ width: 16, height: 16 }} />}
                        {req.text}
                      </RequirementItem>
                    ))}
                  </RequirementList>
                </PasswordRequirements>
              )}
            </InputGroup>

            <InputGroup>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <InputContainer>
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  $hasError={!!error || (!!confirmPassword && !doPasswordsMatch)}
                  $hasTrailingIcon
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </PasswordToggle>
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="dietaryPreference">Dietary Preference</Label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button
                  type="button"
                  variant={dietaryPreference === 'vegetarian' ? 'primary' : 'outline'}
                  onClick={() => setDietaryPreference('vegetarian')}
                  style={{ flex: 1 }}
                >
                  Vegetarian
                </Button>
                <Button
                  type="button"
                  variant={dietaryPreference === 'non-vegetarian' ? 'primary' : 'outline'}
                  onClick={() => setDietaryPreference('non-vegetarian')}
                  style={{ flex: 1 }}
                >
                  Non-Vegetarian
                </Button>
              </div>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                This helps us personalize your diet recommendations and food alternatives.
              </p>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="gender">Gender</Label>
              <InputContainer>
                <Select
                  id="gender"
                  value={gender || ''}
                  onChange={(e) => setGender(e.target.value as 'male' | 'female' | 'prefer_not_to_say' | null)}
                  required
                  $hasError={!!error}
                >
                  <option value="">Select your gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </Select>
              </InputContainer>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                This helps us recommend products tailored to your needs.
              </p>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="prescriptions">Prescription Medications (optional)</Label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Input
                  id="prescriptions"
                  type="text"
                  value={newPrescription}
                  onChange={(e) => setNewPrescription(e.target.value)}
                  placeholder="e.g., Tretinoin, Accutane..."
                  $hasError={!!error}
                  style={{ flex: 1, minWidth: '140px' }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newPrescription.trim() && !prescriptions.includes(newPrescription.trim())) {
                        setPrescriptions(prev => [...prev, newPrescription.trim()]);
                        setNewPrescription('');
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newPrescription.trim() && !prescriptions.includes(newPrescription.trim())) {
                      setPrescriptions(prev => [...prev, newPrescription.trim()]);
                      setNewPrescription('');
                    }
                  }}
                  disabled={!newPrescription.trim()}
                  style={{ flexShrink: 0 }}
                >
                  <Plus size={18} />
                  Add
                </Button>
              </div>
              {prescriptions.length > 0 && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {prescriptions.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0.75rem',
                        background: theme.colors.background.secondary,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border.light}`,
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: theme.typography.fontSize.sm }}>
                        <Pill size={16} color={theme.colors.primary[500]} />
                        {p}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPrescriptions(prev => prev.filter((_, idx) => idx !== i))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: theme.colors.text.tertiary,
                          cursor: 'pointer',
                          padding: '0.25rem',
                        }}
                        aria-label={`Remove ${p}`}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                Add any prescription medications you use. We'll flag ingredient conflicts in product recommendations to keep you safe.
              </p>
            </InputGroup>

            {error && (
              <ErrorMessage>
                <AlertCircle size={16} />
                {error}
              </ErrorMessage>
            )}

            <Button
              type="submit"
              fullWidth
              size="lg"
              disabled={loading || !name || !email || !password || !confirmPassword || !isPasswordValid || !doPasswordsMatch}
            >
              {loading ? (
                <>
                  <LoadingSpinner size={20} />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </Form>

          <Divider>
            <span>or</span>
          </Divider>

          <LinkText>
            <p>Already have an account? <Link to="/signin">Sign in here</Link></p>
          </LinkText>
        </Card>
      </AuthContainer>
    </PageContainer>
  );
};

export default SignUpPage;
